import { Component } from "react";

/**
 * Catches DOM-sync errors caused by browser extensions or translators that
 * mutate the DOM outside React's reconciler (Google Translate, Grammarly,
 * password managers, etc.). Instead of showing the red dev overlay, we log
 * the error and re-render the subtree from scratch by bumping a key.
 *
 * Only catches "NotFoundError: ...removeChild...on 'Node'..." and similar
 * hydration/mutation errors. Everything else bubbles up as usual.
 */
export class DomErrorBoundary extends Component {
  state = { key: 0 };

  static getDerivedStateFromError(error) {
    const msg = String(error?.message || "");
    const isDomSync =
      msg.includes("removeChild") ||
      msg.includes("insertBefore") ||
      msg.includes("Failed to execute") ||
      error?.name === "NotFoundError";
    if (isDomSync) {
      // Trigger remount to recover
      return { needsRemount: true };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Log for observability but do not surface to user for known DOM-sync noise
    console.warn(
      "[DomErrorBoundary] recovered from DOM mutation error:",
      error?.message,
      info?.componentStack?.split("\n").slice(0, 3).join(" | ")
    );
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.needsRemount && !prevState.needsRemount) {
      // Remount subtree on next tick
      requestAnimationFrame(() => {
        this.setState((s) => ({ key: s.key + 1, needsRemount: false }));
      });
    }
  }

  render() {
    if (this.state.needsRemount) {
      // Brief blank tick while we prepare to remount — avoids showing stale UI
      return null;
    }
    return <div key={this.state.key}>{this.props.children}</div>;
  }
}
